import React, { useState } from 'react';
import { MessageCircle, Send, User, Mail, Phone, FileText, CheckCircle } from 'lucide-react';
import clientAxios from '../api/clientAxios';
import loadingGIF from '../assets/images/productos/GIF/GIF.gif'

const ComplaintsSuggestions = () => {
  const [formData, setFormData] = useState({
    type: '', // queja, sugerencia
    name: '',
    email: '',
    phone: '',
    area: '',
    subject: '',
    message: '',
    date: new Date().toISOString().slice(0, 10),
  });
  const [folio, setFolio] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.type) newErrors.type = 'Selecciona el tipo de mensaje';
    if (!formData.subject.trim()) newErrors.subject = 'El asunto es requerido';
    if (!formData.message.trim()) newErrors.message = 'La descripción es requerida';
    if (!formData.area.trim()) newErrors.area = 'El area es requerida';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (validateForm()) {
      setLoading(true);
      setFolio(null); // Limpiar folio antes de enviar
      try {
        const response = await clientAxios.post('/quejas-sugerencias', {
          nombre: formData.name || 'Anónimo',
          correo: formData.email || '',
          telefono: formData.phone || '',
          area: formData.area || '',
          tipo: formData.type,
          asunto: formData.subject,
          mensaje: formData.message
                });
        if (response.data && response.data.folio) {
          setFolio(response.data.folio);
        }
        setSubmitted(true);
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            type: '',
            name: '',
            email: '',
            phone: '',
            area: '',
            subject: '',
            message: '',
            date: new Date().toISOString().slice(0, 10),
          });
          setFolio(null);
        }, 5000);
      } catch (err) {
        // Mostrar mensaje de error más detallado
        if (err.response && err.response.data && err.response.data.error) {
          setErrorMsg('Error: ' + err.response.data.error);
        } else {
          setErrorMsg(err.message || 'Error al enviar el mensaje.');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Enviado exitosamente!</h2>
            <p className="text-gray-600 mb-2">
              Gracias por tu {formData.type === 'queja' ? 'queja' : 'sugerencia'}. Te contactaremos pronto.
            </p>
            {folio && (
              <div className="text-sm text-gray-700 bg-gray-100 rounded-lg p-2 mt-2">
                <span className="font-semibold">Folio:</span> {folio}
              </div>
            )}
          </div>
          <img src={loadingGIF} alt="Cargando..." className="w-25 h-25 mx-auto mt-4"
          style={{ width: '100px', height: '100px' }}
           />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center mb-4">
            <MessageCircle className="h-12 w-12 text-blue-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Centro de Atención</h1>
          </div>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Tu opinión es importante para nosotros. Comparte tus quejas, sugerencias para mejorar nuestros servicios.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-2xl p-8 space-y-10">
          {/* Mensaje de error */}
          {errorMsg && (
            <div className="mb-4 text-center text-red-600 font-semibold bg-red-50 border border-red-200 rounded-lg p-2">
              {errorMsg}
            </div>
          )}
          {/* Datos Generales */}
          <div>
            <h2 className="text-lg font-bold text-blue-700 mb-4 flex items-center gap-2">📋 Datos Generales del Usuario</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <User className="h-4 w-4 inline mr-1" /> Nombre completo (opcional)
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                  placeholder="Tu nombre completo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone className="h-4 w-4 inline mr-1" /> Teléfono de contacto (opcional)
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                  placeholder="+52 123 456 7890"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="h-4 w-4 inline mr-1" /> Correo electrónico (opcional)
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                  placeholder="tu@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Área *
                </label>
                <select
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300"
                >
                  <option value="" disabled>Seleccione un valor...</option>
                  <option value="ADMINISTRACIÓN">ADMINISTRACIÓN</option>
                  <option value="DESTAJO">DESTAJO</option>
                  <option value="OPERACIÓN">OPERACIÓN</option>
                  <option value="PRODUCCIÓN">PRODUCCIÓN</option>
                  <option value="VENTAS">VENTAS</option>
                </select>
                {errors.area && <p className="text-red-500 text-sm mt-1">{errors.area}</p>}
              </div>
            </div>
          </div>

          {/* Detalles de la Queja o Sugerencia */}
          <div>
            <h2 className="text-lg font-bold text-blue-700 mb-4 flex items-center gap-2">🗂 Detalles de la Queja o Sugerencia</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de mensaje *</label>
                <div className="flex gap-4 mt-1">
                  <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${formData.type === 'Queja' ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}>
                    <input
                      type="radio"
                      name="type"
                      value="Queja"
                      checked={formData.type === 'Queja'}
                      onChange={handleInputChange}
                      className="accent-blue-600"
                    />
                    Queja
                  </label>
                  <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${formData.type === 'Sugerencia' ? 'border-green-500 bg-green-50' : 'border-gray-300'}`}>
                    <input
                      type="radio"
                      name="type"
                      value="Sugerencia"
                      checked={formData.type === 'Sugerencia'}
                      onChange={handleInputChange}
                      className="accent-green-600"
                    />
                    Sugerencia
                  </label>
                </div>
                {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <FileText className="h-4 w-4 inline mr-1" /> Asunto o título breve *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.subject ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Resumen breve del tema"
                />
                {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
              </div>
            </div>
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Descripción *</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                className={`w-full h-25 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${errors.message ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Describe tu queja o sugerencia de manera detallada..."
              />
              {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
              <p className="text-sm text-gray-500 mt-2">{formData.message.length}/500 caracteres</p>
            </div>
          </div>

          {/* Folio generado automáticamente */}
          {folio && (
            <div className="text-center mt-4">
              <span className="inline-block bg-gray-100 text-gray-700 rounded-lg px-4 py-2 text-sm font-semibold">Folio generado: {folio}</span>
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-8 text-center">
            <button
              type="submit"
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-4 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <Send className="h-5 w-5 inline mr-2" />
              Enviar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ComplaintsSuggestions;